export const PRIMARY = "#1976D2";
export const PASS_GREEN = "#00B050";
export const PROGRESS_ORANGE = "#FF9900";
export const REVIEW_PURPLE = "#8600F0";
export const RISK_RED = "#C00000";
export const HIGH_RED = "#FF0000";
export const LOW_GREEN = "#92d050";
export const NEUTRAL_GRAY = "#9CA3AF";
export const SLATE = "#64748B";
export const FOR_REVIEW = "#085B96";

export const STATUS_COLORS: Record<string, string> = {
  "Not Started": PRIMARY,
  Open: PRIMARY,
  "In Progress": PROGRESS_ORANGE,
  InProgress: PROGRESS_ORANGE,
  Approval: FOR_REVIEW,
  "Waiting For Approval": FOR_REVIEW,
  "For Review": FOR_REVIEW,
  "In Review": REVIEW_PURPLE,
  Completed: PASS_GREEN,
  Partial: PROGRESS_ORANGE,
  Terminated: SLATE,
  "Not Applicable": NEUTRAL_GRAY,
  Failed: RISK_RED,
  Draft: NEUTRAL_GRAY,
  Findings: PRIMARY,
  KPIs: PRIMARY,
  Controls: PRIMARY,
  "Overdue Tasks": RISK_RED,
  Healthy: PASS_GREEN,
  "At Risk": RISK_RED,
  "Control Health": PRIMARY,
  Ill: HIGH_RED,
  Good: LOW_GREEN,
  "Not Measured": NEUTRAL_GRAY,
};

export const RESULT_COLORS: Record<string, string> = {
  Pass: PASS_GREEN,
  Fail: RISK_RED,
  "No Instances": NEUTRAL_GRAY,
};

export const SEVERITY_COLORS: Record<string, string> = {
  Critical: RISK_RED,
  "Very High": RISK_RED,
  High: HIGH_RED,
  Moderate: PROGRESS_ORANGE,
  Medium: PROGRESS_ORANGE,
  Low: LOW_GREEN,
  "Very Low": PASS_GREEN,
  Negligible: PASS_GREEN,
  "No Data": NEUTRAL_GRAY,
  "N/A": NEUTRAL_GRAY,
};

export const CATEGORY_PALETTE = [
  PRIMARY,
  PASS_GREEN,
  PROGRESS_ORANGE,
  REVIEW_PURPLE,
  RISK_RED,
  FOR_REVIEW,
  LOW_GREEN,
  SLATE,
];

export const AGING_COLORS = ["#F7C4C4", "#F0A0A0", "#E07070", "#D04040", "#C00000"];

export const colorForLabel = (label: string, fallback = PRIMARY) => {
  const key = String(label || "").trim();
  return STATUS_COLORS[key] || RESULT_COLORS[key] || SEVERITY_COLORS[key] || fallback;
};

export const colorsForLabels = (labels: string[]) =>
  labels.map((label, index) => colorForLabel(label, CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]));

const barHeightForCategories = (count: number) => {
  const categories = Math.max(count, 1);
  const plotHeight = 210;
  const maxBarPx = 28;
  const percent = (maxBarPx * categories * 100) / plotHeight;
  return `${Math.round(Math.min(70, Math.max(14, percent)))}%`;
};

export const baseChartOptions = (): Record<string, any> => ({
  chart: {
    toolbar: { show: false },
    fontFamily: "Roboto",
    width: "100%",
    parentHeightOffset: 0,
    redrawOnWindowResize: true,
    redrawOnParentResize: true,
    animations: { enabled: true, speed: 400 },
  },
  legend: {
    position: "bottom",
    fontSize: "12px",
    markers: { width: 10, height: 10, radius: 10 },
    itemMargin: { horizontal: 8, vertical: 2 },
  },
  grid: {
    borderColor: "#eef0f3",
    strokeDashArray: 3,
    padding: { left: 8, right: 12 },
  },
  tooltip: {
    enabled: true,
    followCursor: true,
    fillSeriesColor: false,
    theme: "light",
  },
  dataLabels: { enabled: true },
  noData: { text: "No Records Found" },
});

export const donutOptions = (labels: string[], colors?: string[]): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "donut",
  },
  labels,
  colors: colors?.length ? colors : colorsForLabels(labels),
  dataLabels: { enabled: false },
  plotOptions: {
    pie: {
      donut: {
        size: "70%",
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: "12px",
            fontWeight: 400,
            color: "#6b7280",
            offsetY: 18,
          },
          value: {
            show: true,
            fontSize: "22px",
            fontWeight: 600,
            color: "#111827",
            offsetY: -8,
          },
          total: {
            show: true,
            showAlways: true,
            label: "Total",
            fontSize: "12px",
            fontWeight: 600,
            color: "#6b7280",
            formatter: (w: any) =>
              (w.globals.seriesTotals || []).reduce((sum: number, value: number) => sum + value, 0),
          },
        },
      },
    },
  },
});

export const stackedBarOptions = (categories: string[], seriesNames: string[]): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "bar",
    stacked: true,
    stackType: "normal",
  },
  plotOptions: {
    bar: {
      horizontal: true,
      borderRadius: 2,
      barHeight: barHeightForCategories(categories.length),
      dataLabels: {
        total: {
          enabled: true,
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
      },
    },
  },
  xaxis: { categories },
  yaxis: {
    labels: {
      maxWidth: 140,
      trim: true,
      style: { fontSize: "11px" },
    },
  },
  colors: colorsForLabels(seriesNames),
  dataLabels: {
    enabled: true,
    formatter: (value: number) => (value > 0 ? value : ""),
  },
});

export const barOptions = (categories: string[], color = PRIMARY): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "bar",
  },
  plotOptions: {
    bar: {
      horizontal: true,
      borderRadius: 4,
      borderRadiusApplication: "end",
      barHeight: barHeightForCategories(categories.length),
    },
  },
  xaxis: { categories },
  yaxis: {
    labels: {
      maxWidth: 140,
      trim: true,
      style: { fontSize: "11px" },
    },
  },
  colors: [color],
  legend: { show: false },
  dataLabels: {
    enabled: true,
    formatter: (value: number) => (value > 0 ? value : ""),
  },
});

export const agingBarOptions = (categories: string[]): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "bar",
  },
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadius: 4,
      columnWidth: "45%",
      distributed: true,
    },
  },
  xaxis: {
    categories,
    labels: {
      rotate: -45,
      hideOverlappingLabels: true,
      trim: true,
      style: { fontSize: "11px" },
    },
  },
  colors: AGING_COLORS,
  legend: { show: false },
  dataLabels: { enabled: true },
});

export const lineOptions = (categories: string[]): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "line",
    toolbar: { show: false },
  },
  xaxis: { categories },
  yaxis: {
    min: 0,
    max: 100,
    tickAmount: 10,
    labels: { formatter: (val: number) => `${val}` },
  },
  stroke: { curve: "smooth", width: 2 },
  markers: { size: 4, strokeWidth: 2, hover: { size: 6 } },
  colors: CATEGORY_PALETTE,
  dataLabels: { enabled: false },
  tooltip: {
    ...baseChartOptions().tooltip,
    y: { formatter: (val: number) => `${val}%` },
  },
});

export const percentBarOptions = (categories: string[], color = PASS_GREEN): Record<string, any> => ({
  ...barOptions(categories, color),
  xaxis: {
    categories,
    max: 100,
    labels: {
      formatter: (value: number) => `${value}`,
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (value: number) => `${value}%`,
  },
  tooltip: {
    ...baseChartOptions().tooltip,
    y: { formatter: (val: number) => `${val}%` },
  },
});

export const radarOptions = (categories: string[]): Record<string, any> => ({
  ...baseChartOptions(),
  chart: {
    ...baseChartOptions().chart,
    type: "radar",
    toolbar: { show: false },
  },
  xaxis: {
    categories,
    labels: {
      style: { fontSize: "11px" },
    },
  },
  yaxis: {
    min: 0,
    tickAmount: 4,
    labels: { show: false },
  },
  stroke: { width: 2 },
  fill: { opacity: 0.28 },
  markers: { size: 4 },
  colors: [RISK_RED],
  legend: { show: false },
  dataLabels: {
    enabled: true,
    formatter: (value: number) => (value > 0 ? value : ""),
  },
});
